import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FEATURE_SPECIFICATIONS } from "./factorAnalysisData";

const KMO_PER_VARIABLE = [
  { feature: "weather_dew_point_c", value: 0.476 },
  { feature: "weather_sea_level_pressure_mmhg", value: 0.506 },
  { feature: "weather_station_pressure_mmhg", value: 0.506 },
  { feature: "weather_relative_humidity_pct", value: 0.612 },
];

const KMO_AFTER_EXCLUSION = [
  { step: "0", value: 0.482 },
  { step: "1", value: 0.5098 },
];

const FACTOR_HEATMAP_ROWS = [
  { feature: "weather_dew_point_c", factor1: -0.187, factor2: -0.33, colors: ["#ad298c", "#97149f"] },
  { feature: "weather_relative_humidity_pct", factor1: -0.221, factor2: 0.334, colors: ["#a9258e", "#e97550"] },
  { feature: "weather_sea_level_pressure_mmhg", factor1: 0.981, factor2: 0.095, colors: ["#f5ff00", "#cf4e67"] },
  { feature: "weather_station_pressure_mmhg", factor1: 0.972, factor2: -0.098, colors: ["#f5ff00", "#b03186"] },
];

const EXPLAINED_VARIANCE = [
  { factor: "Фактор 1", value: 49.7 },
  { factor: "Фактор 2", value: 6 },
];

const MODEL_STRATEGY = [
  { model: "all_final_models", include: 8, exclude_leakage: 4, exclude: 8, exclude_duplicate: 0, represented_by_category: 0, include_optional: 0, optional_only: 0 },
  { model: "primary_interpretable_model", include: 9, exclude_leakage: 0, exclude: 0, exclude_duplicate: 3, represented_by_category: 5, include_optional: 0, optional_only: 0 },
  { model: "factor_sensitivity_model", include: 0, exclude_leakage: 0, exclude: 0, exclude_duplicate: 0, represented_by_category: 0, include_optional: 0, optional_only: 1 },
  { model: "sensitivity_model", include: 0, exclude_leakage: 0, exclude: 0, exclude_duplicate: 0, represented_by_category: 0, include_optional: 2, optional_only: 0 },
];

const CORRELATION_FEATURES = ["weather_humidity_z", "weather_dew_point_z", "weather_wind_speed_z", "weather_sea_level_pressure_z"];
const CONTINUOUS_CORRELATIONS = [
  [1, -0.1008583033, -0.0659641415, -0.2196582857],
  [-0.1008583033, 1, -0.1715753476, -0.1883344925],
  [-0.0659641415, -0.1715753476, 1, -0.1188590897],
  [-0.2196582857, -0.1883344925, -0.1188590897, 1],
];

const DECISION_LABELS = {
  include: "Включить",
  include_optional: "Включить опционально",
  optional_only: "Только проверка устойчивости",
  represented_by_category: "Представлен категорией",
  exclude_duplicate: "Исключить дубликат",
  exclude: "Исключить",
  exclude_leakage: "Исключить: утечка",
};

const MODEL_FEATURES = [
  { group: "Непрерывные погодные", feature: "Относительная влажность", specification: "на 10 п.п.", decision: "Включить", state: "include" },
  { group: "Непрерывные погодные", feature: "Точка росы", specification: "на 5 °C", decision: "Включить", state: "include" },
  { group: "Непрерывные погодные", feature: "Скорость ветра", specification: "на 5 м/с", decision: "Включить", state: "include" },
  { group: "Непрерывные погодные", feature: "Давление на уровне моря", specification: "на 10 мм рт. ст.", decision: "Включить", state: "include" },
  { group: "Категории погоды", feature: "Осадки", specification: "единая взаимоисключающая категория", decision: "Включить", state: "include" },
  { group: "Категории погоды", feature: "Видимость", specification: "категория с отдельным значением пропуска", decision: "Включить", state: "include" },
  { group: "Бинарные признаки", feature: "Влажное покрытие, снежный покров, дымка", specification: "самостоятельные признаки", decision: "Включить", state: "include" },
  { group: "Факторные оценки", feature: "Глобальный фактор 1", specification: "барический режим", decision: "Только проверка устойчивости", state: "optional" },
  { group: "Факторные оценки", feature: "Глобальный фактор 2", specification: "5,99% объяснённой дисперсии", decision: "Исключить", state: "exclude" },
  { group: "Дублирующие / служебные", feature: "Давление на станции, пороговые флаги, service-поля", specification: "дублирование или качество данных", decision: "Исключить", state: "exclude" },
  { group: "Утечка результата", feature: "Число погибших и пострадавших", specification: "последствие ДТП", decision: "Исключить", state: "exclude" },
];

function ReportCard({ title, children, className = "" }) {
  return (
    <section className={`statistics-report-card ${className}`.trim()}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="statistics-report-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((item) => (
        <div key={item.dataKey}><span style={{ background: item.color }} />{item.name}: {Number(item.value ?? 0).toFixed(3)}</div>
      ))}
    </div>
  );
}

function FactorLoadingsHeatmap() {
  return (
    <div className="statistics-factor-heatmap" role="img" aria-label="Тепловая карта факторных нагрузок погодных признаков">
      <div className="statistics-factor-heatmap-table">
        <div className="statistics-factor-heatmap-blank" />
        <div className="statistics-factor-heatmap-column-title">Фактор 1</div>
        <div className="statistics-factor-heatmap-column-title">Фактор 2</div>
        {FACTOR_HEATMAP_ROWS.map((row) => (
          <div className="statistics-factor-heatmap-row" key={row.feature}>
            <span>{row.feature}</span>
            <b style={{ background: row.colors[0] }}>{row.factor1.toFixed(3)}</b>
            <b style={{ background: row.colors[1] }}>{row.factor2.toFixed(3)}</b>
          </div>
        ))}
      </div>
      <div className="statistics-factor-heatmap-legend" aria-hidden="true"><span>Нагрузка</span><i /><em>0.5</em><em>0</em><em>-0.5</em></div>
    </div>
  );
}

function correlationColor(value) {
  if (value >= 0.99) return "#f5ff00";
  if (value <= -0.2) return "#ad298c";
  if (value <= -0.15) return "#b63385";
  if (value <= -0.1) return "#be3b7e";
  return "#c44179";
}

function CorrelationHeatmap() {
  const format = (value) => (value === 1 ? "1" : value.toFixed(2).replace(/0$/, ""));
  return (
    <div className="statistics-factor-correlation" role="img" aria-label="Корреляции финальных непрерывных погодных признаков">
      <div className="statistics-factor-correlation-table">
        <div />
        {CORRELATION_FEATURES.map((feature) => <span className="statistics-factor-correlation-column" key={feature}>{feature}</span>)}
        {CONTINUOUS_CORRELATIONS.map((row, rowIndex) => (
          <div className="statistics-factor-correlation-row" key={CORRELATION_FEATURES[rowIndex]}>
            <span>{CORRELATION_FEATURES[rowIndex]}</span>
            {row.map((value, index) => <b className={value > 0.9 ? "is-light" : ""} key={`${rowIndex}-${index}`} style={{ background: correlationColor(value) }}>{format(value)}</b>)}
          </div>
        ))}
      </div>
      <div className="statistics-factor-correlation-legend" aria-hidden="true"><span>Корреляция</span><i /><em>1</em><em>0.5</em><em>0</em><em>-0.5</em><em>-1</em></div>
    </div>
  );
}

const FACTOR_FEATURES = FEATURE_SPECIFICATIONS.length ? FEATURE_SPECIFICATIONS : MODEL_FEATURES;

export default function FactorAnalysisReportSection() {
  return (
    <div className="statistics-factor-analysis-report">
      <div className="statistics-report-source-note">
        <strong>Источник раздела.</strong> Технические витрины факторного анализа погодных признаков коллеги.
        Результаты относятся к аналитической выборке ДТП 2020–2024, а не к текущим данным рабочей БД.
      </div>

      <div className="statistics-factor-kpi-grid">
        <article className="statistics-factor-kpi"><span>Итоговый KMO</span><strong>0.5098</strong><small>Пограничная факторизуемость</small></article>
        <article className="statistics-factor-kpi"><span>Bartlett p-value</span><strong>&lt;0.001</strong><small>Корреляционная матрица не единичная</small></article>
        <article className="statistics-factor-kpi"><span>Финальных признаков EFA</span><strong>4</strong><small>После удаления скорости ветра</small></article>
        <article className="statistics-factor-kpi"><span>Кумулятивная дисперсия</span><strong>55.7%</strong><small>Два фактора</small></article>
        <article className="statistics-factor-kpi"><span>Корреляция давлений</span><strong>0.937</strong><small>Высокое дублирование</small></article>
      </div>

      <section className="statistics-factor-conclusion">
        <h3>Главный вывод</h3>
        <p>Глобальная латентная структура погодных признаков подтверждена лишь погранично. Поэтому факторные оценки не стали основной спецификацией: в моделях используются отдельные, физически интерпретируемые признаки.</p>
      </section>

      <div className="statistics-report-section-heading"><h3>Диагностика факторизуемости</h3></div>
      <div className="statistics-report-chart-grid">
        <ReportCard title="Как изменился KMO после исключения слабого признака" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={KMO_AFTER_EXCLUSION} margin={{ top: 20, right: 18, bottom: 18, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="step" type="category" label={{ value: "Шаг", position: "insideBottom", offset: -7, fontSize: 12 }} />
              <YAxis domain={[0.482, 0.5115]} ticks={[0.485, 0.49, 0.495, 0.5, 0.505, 0.51]} tick={{ fontSize: 12 }} label={{ value: "KMO", angle: -90, position: "insideLeft", offset: 0, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={0.5} stroke="#334155" strokeDasharray="7 6" label={{ value: "Минимально приемлемый уровень 0.50", position: "insideTopRight", fontSize: 11, fill: "#334155" }} />
              <Line type="linear" dataKey="value" name="KMO" stroke="#636efa" strokeWidth={2} dot={{ r: 3, fill: "#636efa" }} activeDot={{ r: 4 }}>
                <LabelList dataKey="value" position="top" formatter={(value) => Number(value).toFixed(value < 0.5 ? 3 : 2)} fontSize={11} fill="#334155" />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Индивидуальная мера выборочной адекватности" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={KMO_PER_VARIABLE} layout="vertical" margin={{ top: 20, right: 30, bottom: 18, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" dataKey="value" domain={[0, 0.65]} tick={{ fontSize: 12 }} label={{ value: "KMO MSA", position: "insideBottom", offset: -7, fontSize: 12 }} />
              <YAxis dataKey="feature" type="category" width={210} tick={{ fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine x={0.5} stroke="#334155" strokeDasharray="7 6" />
              <Bar dataKey="value" name="KMO MSA" fill="#636efa" radius={[0, 0, 0, 0]}>
                <LabelList dataKey="value" position="insideRight" formatter={(value) => Number(value).toFixed(3)} fontSize={11} fill="#ffffff" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Структура выделенных факторов</h3></div>
      <div className="statistics-factor-structure-grid">
        <ReportCard title="Факторные нагрузки погодных признаков" className="statistics-report-chart-card">
          <FactorLoadingsHeatmap />
        </ReportCard>
        <ReportCard title="Доля объяснённой дисперсии" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={370}>
            <BarChart data={EXPLAINED_VARIANCE} margin={{ top: 22, right: 18, bottom: 12, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="factor" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 52]} tick={{ fontSize: 11 }} label={{ value: "%", angle: -90, position: "insideLeft", offset: 0, fontSize: 11 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" name="Дисперсия, %" fill="#636efa">
                <LabelList dataKey="value" position="insideTop" formatter={(value) => `${value}%`} fill="#ffffff" fontSize={11} fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Интерпретация" className="statistics-report-findings-card statistics-factor-interpretation-card">
          <p>Первый фактор почти полностью отражает барический режим: нагрузки давления на станции и давления, приведённого к уровню моря, близки к 1. Второй фактор слаб и содержательно нестабилен.</p>
          <div className="statistics-factor-loading-note">Глобальная латентная структура подтверждена лишь погранично. Поэтому факторные оценки оставлены только для sensitivity-анализа.</div>
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Финальная стратегия признаков</h3></div>
      <div className="statistics-factor-final-grid">
        <ReportCard title="Финальная спецификация признаков" className="statistics-report-chart-card">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={MODEL_STRATEGY} margin={{ top: 30, right: 18, bottom: 60, left: 4 }}>
              <CartesianGrid stroke="#e1e9e6" strokeDasharray="3 3" />
              <XAxis dataKey="model" tick={{ fontSize: 10, angle: -18, textAnchor: "end" }} label={{ value: "Модельная спецификация", position: "insideBottom", offset: -43, fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: "Число признаков", angle: -90, position: "insideLeft", offset: 0, fontSize: 12 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend verticalAlign="top" height={46} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="include" name="include" stackId="features" fill="#636efa"><LabelList dataKey="include" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#ffffff" /></Bar>
              <Bar dataKey="exclude_leakage" name="exclude_leakage" stackId="features" fill="#ef553b"><LabelList dataKey="exclude_leakage" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#ffffff" /></Bar>
              <Bar dataKey="exclude" name="exclude" stackId="features" fill="#00cc96"><LabelList dataKey="exclude" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#ffffff" /></Bar>
              <Bar dataKey="represented_by_category" name="represented_by_category" stackId="features" fill="#19d3f3"><LabelList dataKey="represented_by_category" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#16324f" /></Bar>
              <Bar dataKey="exclude_duplicate" name="exclude_duplicate" stackId="features" fill="#ffa15a"><LabelList dataKey="exclude_duplicate" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#16324f" /></Bar>
              <Bar dataKey="optional_only" name="optional_only" stackId="features" fill="#ab63fa"><LabelList dataKey="optional_only" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#ffffff" /></Bar>
              <Bar dataKey="include_optional" name="include_optional" stackId="features" fill="#ff6692"><LabelList dataKey="include_optional" position="inside" formatter={(value) => value || ""} fontSize={11} fill="#ffffff" /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </ReportCard>
        <ReportCard title="Корреляции финальных непрерывных погодных признаков" className="statistics-report-chart-card">
          <CorrelationHeatmap />
        </ReportCard>
      </div>

      <div className="statistics-report-section-heading"><h3>Научный вывод</h3></div>
      <section className="statistics-factor-scientific-conclusion"><strong>Глобальная латентная структура погоды подтверждена лишь погранично и не используется как основная спецификация.</strong> Основная модель использует отдельные интерпретируемые погодные признаки. Из двух сильно коррелированных показателей давления оставлено давление, приведённое к уровню моря. Первый глобальный фактор допускается только как дополнительная проверка устойчивости, второй исключён.</section>

      <div className="statistics-report-section-heading"><h3>Спецификация признаков</h3></div>
      <div className="statistics-report-table-wrap">
        <table className="statistics-report-table statistics-factor-table">
          <thead><tr><th>Признак</th><th>Группа</th><th>Спецификация</th><th>Решение</th><th>Обоснование</th></tr></thead>
          <tbody>{FACTOR_FEATURES.map((row) => (
            <tr key={`${row.group}-${row.feature}`}><th>{row.feature}</th><td>{row.group}</td><td>{row.specification}</td><td><span className={`statistics-factor-decision is-${row.decision}`}>{DECISION_LABELS[row.decision] || row.decision}</span></td><td>{row.reason}</td></tr>
          ))}</tbody>
        </table>
      </div>

    </div>
  );
}
